import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Modal from 'react-modal';
import WorkflowEditor from './components/WorkflowEditor';
import WorkflowRunner from './components/WorkflowRunner';

// Set up React Modal
Modal.setAppElement('#root');

const WorkflowBuilder = () => {
  const [searchParams] = useSearchParams();
  const [mode, setMode] = useState(searchParams.get('mode') || 'list');
  const [workflows, setWorkflows] = useState([]);
  const [currentWorkflow, setCurrentWorkflow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkflow, setNewWorkflow] = useState({ title: '', description: '' });

  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  const loadWorkflows = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/workflows`);
      const data = await response.json();
      setWorkflows(data);
    } catch (error) {
      console.error('Error loading workflows:', error);
    } finally {
      setLoading(false);
    }
  }, [backendUrl]);

  // Load workflows on component mount
  useEffect(() => {
    loadWorkflows();
  }, [loadWorkflows]);

  const saveWorkflow = async (workflowData) => {
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/workflows/${workflowData.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowData),
      });
      const updatedWorkflow = await response.json();
      setWorkflows(workflows.map(w => w.id === updatedWorkflow.id ? updatedWorkflow : w));
      setMode('list');
    } catch (error) {
      console.error('Error saving workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWorkflow = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await fetch(`${backendUrl}/api/workflows`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(newWorkflow),
      });
      const data = await response.json();
      setWorkflows([...workflows, data]);
      setNewWorkflow({ title: '', description: '' });
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const deleteWorkflow = async (workflowId) => {
    if (!window.confirm('Are you sure you want to delete this workflow?')) return;
    
    try {
      setLoading(true);
      await fetch(`${backendUrl}/api/workflows/${workflowId}`, {
        method: 'DELETE',
      });
      setWorkflows(workflows.filter(w => w.id !== workflowId));
    } catch (error) {
      console.error('Error deleting workflow:', error);
    } finally {
      setLoading(false);
    }
  };

  const exportWorkflow = async (workflowId) => {
    try {
      const response = await fetch(`${backendUrl}/api/workflows/${workflowId}/export`);
      const data = await response.json();
      
      // Create download link
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `workflow-${data.title.replace(/\s+/g, '-').toLowerCase()}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting workflow:', error);
    }
  };

  const importWorkflow = async (file) => {
    try {
      const text = await file.text();
      const workflowData = JSON.parse(text);
      
      const response = await fetch(`${backendUrl}/api/workflows/import`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(workflowData),
      });
      const data = await response.json();
      setWorkflows([...workflows, data]);
    } catch (error) {
      console.error('Error importing workflow:', error);
      alert('Error importing workflow. Please check the file format.');
    }
  };

  const WorkflowListView = () => (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="flex justify-between items-center mb-8">
        <h2 className="text-3xl font-bold text-white">My Workflows</h2>
        <div className="flex gap-4">
          <label className="px-4 py-2 bg-slate-700 text-white rounded-xl cursor-pointer hover:bg-slate-600 transition-colors">
            Import Workflow
            <input
              type="file"
              accept=".json"
              onChange={(e) => e.target.files[0] && importWorkflow(e.target.files[0])}
              className="hidden"
            />
          </label>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-6 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:scale-105 transition-transform"
          >
            Create New Workflow
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="text-slate-400 mt-4">Loading workflows...</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((workflow) => (
            <div key={workflow.id} className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-slate-600/50 transition-all duration-300">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-white">{workflow.title}</h3>
                <div className="flex items-center gap-2">
                  {workflow.is_published && (
                    <span className="px-2 py-1 bg-green-500/20 text-green-300 rounded-full text-xs">
                      Published
                    </span>
                  )}
                </div>
              </div>
              
              <p className="text-slate-400 mb-4 text-sm">{workflow.description}</p>
              
              <div className="text-xs text-slate-500 mb-4">
                <p>Created: {new Date(workflow.created_at).toLocaleDateString()}</p>
                <p>Nodes: {workflow.nodes.length}</p>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => {
                    setCurrentWorkflow(workflow);
                    setMode('edit');
                  }}
                  className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-sm hover:bg-blue-500/30 transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => {
                    setCurrentWorkflow(workflow);
                    setMode('use');
                  }}
                  className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-sm hover:bg-green-500/30 transition-colors"
                >
                  Use
                </button>
                <button
                  onClick={() => exportWorkflow(workflow.id)}
                  className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm hover:bg-purple-500/30 transition-colors"
                >
                  Export
                </button>
                <button
                  onClick={() => deleteWorkflow(workflow.id)}
                  className="px-3 py-1 bg-red-500/20 text-red-300 rounded-full text-sm hover:bg-red-500/30 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          
          {workflows.length === 0 && (
            <div className="col-span-full text-center py-12">
              <div className="text-6xl mb-4">🌳</div>
              <h3 className="text-xl font-semibold text-white mb-2">No workflows yet</h3>
              <p className="text-slate-400 mb-6">Create your first workflow to get started!</p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:scale-105 transition-transform"
              >
                Create Your First Workflow
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  const WorkflowEditView = () => (
    <WorkflowEditor
      workflow={currentWorkflow}
      onSave={saveWorkflow}
      onCancel={() => setMode('list')}
    />
  );

  const WorkflowUseView = () => (
    <WorkflowRunner
      workflow={currentWorkflow}
      onClose={() => setMode('list')}
    />
  );

  return (
    <div className="bg-slate-900 text-white min-h-screen">
      {mode === 'list' && (
        <>
          <nav className="bg-slate-900/90 backdrop-blur-lg border-b border-slate-800/50">
            <div className="max-w-7xl mx-auto px-6 py-4">
              <div className="flex items-center justify-between">
                <Link to="/" className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                  Fabricated.Space
                </Link>
                <div className="flex items-center gap-4">
                  <Link to="/experimental" className="text-slate-300 hover:text-white transition-colors duration-200">
                    ← Experimental Projects
                  </Link>
                </div>
              </div>
            </div>
          </nav>
          
          <section className="py-12 px-6">
            <div className="max-w-7xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-600 bg-clip-text text-transparent">
                Workflow Builder
              </h1>
              <p className="text-xl text-slate-400 mb-8">
                Create interactive decision trees and guided workflows with visual editing and export capabilities.
              </p>
            </div>
          </section>
          
          <WorkflowListView />
        </>
      )}
      
      {mode === 'edit' && <WorkflowEditView />}
      {mode === 'use' && <WorkflowUseView />}

      {/* Create Workflow Modal */}
      <Modal
        isOpen={showCreateModal}
        onRequestClose={() => setShowCreateModal(false)}
        className="bg-slate-800 rounded-2xl p-8 max-w-md mx-auto mt-20 border border-slate-700"
        overlayClassName="fixed inset-0 bg-black/50 flex items-start justify-center p-4"
      >
        <h2 className="text-2xl font-bold text-white mb-6">Create New Workflow</h2>
        <form onSubmit={createWorkflow}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Title
            </label>
            <input
              type="text"
              value={newWorkflow.title}
              onChange={(e) => setNewWorkflow({...newWorkflow, title: e.target.value})}
              className="w-full px-4 py-2 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-blue-500 focus:outline-none"
              required
            />
          </div>
          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-300 mb-2">
              Description
            </label>
            <textarea
              value={newWorkflow.description}
              onChange={(e) => setNewWorkflow({...newWorkflow, description: e.target.value})}
              className="w-full px-4 py-2 bg-slate-700 text-white rounded-xl border border-slate-600 focus:border-blue-500 focus:outline-none"
              rows={3}
              required
            />
          </div>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => setShowCreateModal(false)}
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-xl hover:bg-slate-600 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl hover:scale-105 transition-transform disabled:opacity-50"
            >
              {loading ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default WorkflowBuilder;